import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight, Truck, ShieldCheck, Radio, Handshake, MapPin } from "lucide-react";
import RouteCanvas from "@/components/RouteCanvas";
import Marquee from "@/components/Marquee";
import { Reveal, MaskLine, SectionLabel } from "@/components/motion";
import { SERVICES, INDUSTRIES, STEPS, IMAGES } from "@/lib/data";

const STATS = [
  ["24/7", "Dispatch Oversight"],
  ["CAN / US", "Cross-Border Lanes"],
  ["08", "Service Lines"],
  ["01", "Point of Contact"],
];

const WHY = [
  { icon: Radio, title: "Communication", desc: "Proactive updates at every milestone. No chasing." },
  { icon: ShieldCheck, title: "Reliability", desc: "Careful carrier selection and follow-through on every load." },
  { icon: Handshake, title: "Relationships", desc: "Long-term partnerships over one-off transactions." },
  { icon: Truck, title: "Vetted Capacity", desc: "Qualified, insured carriers with suitable equipment and authority." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <main data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative flex min-h-screen flex-col justify-end overflow-hidden pt-16">
        <motion.div style={{ y: imgY }} className="absolute inset-0">
          <img src={IMAGES.heroTruck} alt="Freight truck on highway" className="h-full w-full object-cover opacity-45" />
          <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/60 to-asphalt/30" />
        </motion.div>
        <RouteCanvas className="opacity-50" />
        <div className="noise-overlay pointer-events-none absolute inset-0 opacity-[0.07]" />

        <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-16 lg:px-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="mb-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.3em] text-brass"
            data-testid="hero-eyebrow"
          >
            <MapPin size={13} className="text-hazard" /> Calgary, AB — Independent Freight Brokerage
          </motion.p>

          <h1 className="font-display text-4xl font-black uppercase leading-[1.02] tracking-tight text-bright sm:text-5xl lg:text-7xl" data-testid="hero-headline">
            <MaskLine delay={0.35}>Connecting <span className="text-hazard">Freight.</span></MaskLine>
            <MaskLine delay={0.5}>Coordinating <span className="text-hazard">Capacity.</span></MaskLine>
            <MaskLine delay={0.65}>Delivering <span className="text-stroke-hazard">Solutions.</span></MaskLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.95, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-6 max-w-xl text-base leading-relaxed text-[#9CA3AF] sm:text-lg"
          >
            Need freight moved? We connect you with vetted carrier capacity across Canada and North America — one professional point of contact, end to end.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/shippers"
              data-testid="hero-quote-cta"
              className="group flex items-center gap-2 bg-hazard px-7 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-asphalt transition-colors duration-300 hover:bg-hazard-dark"
            >
              Request a Quote
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
            <Link
              to="/carriers"
              data-testid="hero-carrier-cta"
              className="border border-line-light px-7 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-bright transition-colors duration-300 hover:border-hazard hover:text-hazard"
            >
              Join Carrier Network
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.3, duration: 1 }}
            className="mt-14 grid grid-cols-2 border-t border-line/80 lg:grid-cols-4"
            data-testid="hero-stats"
          >
            {STATS.map(([v, l], i) => (
              <div key={l} className={`py-5 pr-6 ${i > 0 ? "border-l border-line/80 pl-6" : ""}`}>
                <p className="font-display text-2xl font-extrabold text-hazard sm:text-3xl">{v}</p>
                <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#9CA3AF]">{l}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <Marquee />

      {/* WHY ELT — BENTO */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="why-section">
        <Reveal>
          <SectionLabel>// Why Work With ELT</SectionLabel>
          <h2 className="mt-3 max-w-2xl font-display text-3xl font-extrabold uppercase tracking-tight text-bright sm:text-4xl lg:text-5xl">
            One point of coordination. <span className="text-hazard">Zero chasing.</span>
          </h2>
        </Reveal>

        <div className="mt-12 grid grid-cols-12 gap-6 lg:gap-8">
          <Reveal className="col-span-12 lg:col-span-8 lg:row-span-2" delay={0.05}>
            <div className="group relative h-full min-h-[320px] overflow-hidden border border-line bg-panel">
              <img src={IMAGES.sunsetHighway} alt="Highway at dusk" className="absolute inset-0 h-full w-full object-cover opacity-40 transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-asphalt via-asphalt/40 to-transparent" />
              <div className="relative flex h-full flex-col justify-end p-8">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-brass">The ELT Standard</p>
                <h3 className="mt-3 font-display text-2xl font-bold uppercase text-bright sm:text-3xl">
                  We understand your freight, source the right capacity, and keep every party informed — throughout the shipment.
                </h3>
                <p className="mt-3 max-w-lg text-sm leading-relaxed text-[#9CA3AF]">
                  Load requirements, carrier sourcing, booking, tracking and delivery follow-up. You deal with one professional point of contact instead of many.
                </p>
              </div>
            </div>
          </Reveal>

          <Reveal className="col-span-12 sm:col-span-6 lg:col-span-4" delay={0.15}>
            <div className="flex h-full flex-col justify-between border border-line bg-panel p-8 transition-colors duration-300 hover:bg-panel-hover">
              <p className="font-display text-5xl font-black text-hazard">01</p>
              <div>
                <h3 className="font-display text-xl font-bold uppercase text-bright">Professionalism</h3>
                <p className="mt-2 text-sm text-[#9CA3AF]">Honest, respectful dealings with shippers and carriers alike.</p>
              </div>
            </div>
          </Reveal>

          {WHY.map((w, i) => (
            <Reveal key={w.title} className="col-span-12 sm:col-span-6 lg:col-span-4" delay={0.2 + i * 0.07}>
              <div className="group h-full border border-line bg-panel p-8 transition-colors duration-300 hover:bg-panel-hover">
                <w.icon size={26} className="text-hazard transition-transform duration-300 group-hover:-translate-y-1" />
                <h3 className="mt-5 font-display text-xl font-bold uppercase text-bright">{w.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#9CA3AF]">{w.desc}</p>
              </div>
            </Reveal>
          ))}

          <Reveal className="col-span-12" delay={0.1}>
            <div className="relative overflow-hidden border border-line bg-panel">
              <div className="hazard-stripes h-1.5 w-full" />
              <div className="flex flex-col items-start justify-between gap-4 p-8 sm:flex-row sm:items-center">
                <p className="font-display text-xl font-bold uppercase text-bright sm:text-2xl">
                  Single urgent load or regular weekly freight — <span className="text-hazard">we scale to fit.</span>
                </p>
                <Link to="/contact" data-testid="why-cta-link" className="shrink-0 font-mono text-xs font-bold uppercase tracking-[0.2em] text-brass transition-colors hover:text-hazard">
                  Talk to Dispatch →
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="border-t border-line bg-panel/40">
        <div className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="services-section">
          <Reveal className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
            <div>
              <SectionLabel>// Freight Services</SectionLabel>
              <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-bright sm:text-4xl lg:text-5xl">
                Capacity for every load
              </h2>
            </div>
            <Link to="/services" data-testid="services-view-all" className="font-mono text-xs font-bold uppercase tracking-[0.2em] text-brass transition-colors hover:text-hazard">
              View All Services →
            </Link>
          </Reveal>

          <div className="mt-12 grid gap-px border border-line bg-line sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <Reveal key={s.id} delay={i * 0.05} y={16}>
                <Link
                  to="/services"
                  data-testid={`service-card-${s.id}`}
                  className="group flex h-full flex-col justify-between bg-asphalt p-7 transition-colors duration-300 hover:bg-panel"
                >
                  <div>
                    <p className="font-mono text-[10px] uppercase tracking-[0.22em] text-brass">{s.tag}</p>
                    <h3 className="mt-3 font-display text-lg font-bold uppercase leading-snug text-bright transition-colors group-hover:text-hazard">
                      {s.title}
                    </h3>
                    <p className="mt-2 text-sm leading-relaxed text-[#9CA3AF]">{s.desc}</p>
                  </div>
                  <ArrowUpRight size={18} className="mt-6 text-dim transition-all duration-300 group-hover:translate-x-1 group-hover:-translate-y-1 group-hover:text-hazard" />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-5 py-24 lg:px-8" data-testid="process-section">
        <Reveal>
          <SectionLabel>// How It Works</SectionLabel>
          <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-bright sm:text-4xl lg:text-5xl">
            From spec to <span className="text-hazard">proof of delivery</span>
          </h2>
        </Reveal>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((s, i) => (
            <Reveal key={s.step} delay={i * 0.1}>
              <div className="group relative border border-line bg-panel p-7 transition-colors duration-300 hover:bg-panel-hover">
                <div className="absolute left-0 top-0 h-1 w-0 bg-hazard transition-all duration-500 group-hover:w-full" />
                <p className="font-mono text-sm font-bold text-hazard">{s.step}</p>
                <h3 className="mt-4 font-display text-xl font-bold uppercase text-bright">{s.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-[#9CA3AF]">{s.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="border-t border-line bg-panel/40">
        <div className="mx-auto grid max-w-7xl gap-12 px-5 py-24 lg:grid-cols-2 lg:px-8" data-testid="industries-section">
          <Reveal>
            <SectionLabel>// Industries We Serve</SectionLabel>
            <h2 className="mt-3 font-display text-3xl font-extrabold uppercase tracking-tight text-bright sm:text-4xl lg:text-5xl">
              Built for Western Canada's industrial corridors
            </h2>
            <p className="mt-5 max-w-md text-base leading-relaxed text-[#9CA3AF]">
              Based in Calgary at the centre of the region's energy, construction and industrial freight lanes — serving customers and carriers across Canada and North American markets.
            </p>
            <div className="relative mt-8 overflow-hidden border border-line">
              <img src={IMAGES.calgary} alt="Calgary and the Rocky Mountains" className="h-56 w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-asphalt/80 to-transparent" />
              <p className="absolute bottom-4 left-4 font-mono text-[11px] uppercase tracking-[0.25em] text-brass">HQ — Calgary, Alberta</p>
            </div>
          </Reveal>
          <div className="flex flex-col justify-center">
            {INDUSTRIES.map((ind, i) => (
              <Reveal key={ind} delay={i * 0.06} y={14}>
                <div className="group flex items-center justify-between border-b border-line py-5 transition-colors" data-testid={`industry-${i}`}>
                  <div className="flex items-center gap-5">
                    <span className="font-mono text-xs text-hazard">0{i + 1}</span>
                    <span className="font-display text-xl font-bold uppercase text-bright transition-transform duration-300 group-hover:translate-x-2 group-hover:text-hazard sm:text-2xl">
                      {ind}
                    </span>
                  </div>
                  <ArrowUpRight size={18} className="text-dim opacity-0 transition-all duration-300 group-hover:text-hazard group-hover:opacity-100" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden border-t border-line">
        <div className="hazard-stripes h-2 w-full" />
        <RouteCanvas className="opacity-30" />
        <div className="relative mx-auto max-w-7xl px-5 py-24 text-center lg:px-8" data-testid="cta-section">
          <Reveal>
            <h2 className="mx-auto max-w-3xl font-display text-3xl font-black uppercase leading-tight text-bright sm:text-4xl lg:text-6xl">
              Need freight moved? <span className="text-hazard">We have the capacity.</span>
            </h2>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/shippers" data-testid="cta-quote-btn" className="bg-hazard px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-asphalt transition-colors duration-300 hover:bg-hazard-dark">
                Request a Quote
              </Link>
              <Link to="/carriers" data-testid="cta-carrier-btn" className="border border-line-light px-8 py-4 font-mono text-sm font-bold uppercase tracking-[0.15em] text-bright transition-colors duration-300 hover:border-hazard hover:text-hazard">
                Haul for ELT
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
